/**
 * Backend DTO → domain type transforms for the Devices slice. Defensive: every
 * field has a sane default so a sparse mock or partial backend row never throws
 * in the UI. Number-ish fields go through the shared `toNumber` normalizer.
 */
import { toNumber } from "@/features/account/api/mappers";
import type {
  AirplaneTriggerLog,
  Device,
  DeviceBucketData,
  DeviceLoadRow,
  DeviceLoadStats,
  DeviceNotification,
  DeviceNotificationsPage,
  DeviceSettings,
  DeviceSuccessRates,
  DevicesListResult,
  FleetSummary,
  LaunchAutomationResult,
  MarketDevice,
  MarketSearchResult,
  ProxyConnection,
  RentDuration,
  SuccessRateCell,
  TriggerLogsResult,
} from "@/features/devices/types";

import type {
  AirplaneTriggerLogDto,
  DeviceBucketResponseDto,
  DeviceLoadRowDto,
  DeviceRowDto,
  DeviceNotificationDto,
  DeviceNotificationsResponseDto,
  DeviceSettingsDto,
  FleetSummaryDto,
  GetDevicesResponseDto,
  LaunchAutomationResponseDto,
  LoadStatsResponseDto,
  MarketDeviceDto,
  MarketResponseDto,
  ProxyBandwidthResponseDto,
  ProxyConnectionDto,
  SuccessRateCellDto,
  SuccessRatesResponseDto,
  TriggerLogsResponseDto,
} from "./dto";

export function mapDevice(dto: DeviceRowDto): Device {
  return {
    remoteDeviceId: dto.remote_device_id ?? "",
    deviceId: dto.device_id ?? "",
    name: dto.name ?? "",
    brand: dto.brand ?? "",
    model: dto.model ?? "",
    country: dto.country ?? "",
    online: Boolean(dto.online),
    status: dto.status ?? "stopped",
    sdkVersion: dto.sdkVersion ?? 0,
    androidVersion: dto.version ?? "",
    appVersion: dto.appVersion ?? "",
    ramMb: dto.ramMb ?? 0,
    numberOfCores: dto.numberOfCores ?? 0,
    processor: dto.processor ?? "",
    isEmulator: Boolean(dto.isEmulator),
    batteryLevel: dto.batteryLevel ?? null,
    isCharging: Boolean(dto.isCharging),
    networkType: dto.networkType ?? "",
    inUse: Boolean(dto.is_inuse),
    inUseByTitle: dto.inuse_by_title ?? "",
    onMarket: Boolean(dto.is_onMarket),
    rented: Boolean(dto.is_rented),
    lastMonthEarning: toNumber(dto.lastMonthEarning, 0),
    ownerUserId: dto.user_id ?? "",
    rentByUserId: dto.rent_by_user_id ?? "",
    phoneNumber: dto.phoneNumber ?? null,
    isAssistant: Boolean(dto.isAssistant),
    isAdbConnected: Boolean(dto.isAdbConnected),
    rolloutGroup: dto.rollout_group ?? null,
    lastSeenAt: dto.batteryUpdatedAt ?? null,
  };
}

export function mapDevices(dto: GetDevicesResponseDto): Device[] {
  return (dto.devices ?? []).map(mapDevice);
}

/** The fleet list plus the account-level `ppaDevicePresent` flag. `mapDevices`
 * is kept separate (the admin slice reuses it) — this only adds the flag. */
export function mapDevicesResponse(
  dto: GetDevicesResponseDto,
): DevicesListResult {
  return {
    devices: mapDevices(dto),
    ppaDevicePresent: Boolean(dto.ppaDevicePresent),
    totalDevices: dto.totaldevice ?? 0,
  };
}

function mapCell(dto: SuccessRateCellDto): SuccessRateCell {
  return {
    successfulTasks: dto.successful_tasks ?? 0,
    failedTasks: dto.failed_tasks ?? 0,
    totalTasks: dto.total_tasks ?? 0,
    successRate: toNumber(dto.success_rate, 0),
  };
}

export function mapSuccessRates(
  dto: SuccessRatesResponseDto,
): DeviceSuccessRates {
  const data = dto.data ?? {};
  const matrix: DeviceSuccessRates["matrix"] = {};
  for (const [jobId, byDevice] of Object.entries(data.matrix ?? {})) {
    matrix[jobId] = {};
    for (const [deviceId, cell] of Object.entries(byDevice ?? {})) {
      matrix[jobId][deviceId] = mapCell(cell);
    }
  }
  return {
    devices: (data.devices ?? []).map((d) => ({
      remoteDeviceId: d.remote_device_id ?? "",
      name: d.name ?? "",
      brand: d.brand ?? "",
      model: d.model ?? "",
      ownerUsername: d.owner?.username ?? "",
    })),
    jobs: (data.jobs ?? []).map((j) => ({
      jobId: j.job_id ?? "",
      title: j.title ?? "",
    })),
    matrix,
    devicesTotal: data.pagination?.devices?.total ?? 0,
    jobsTotal: data.pagination?.jobs?.total ?? 0,
    devicesHasMore: Boolean(data.pagination?.devices?.hasMore),
    jobsHasMore: Boolean(data.pagination?.jobs?.hasMore),
  };
}

function mapLoadRow(dto: DeviceLoadRowDto): DeviceLoadRow {
  return {
    remoteDeviceId: dto.remote_device_id ?? "",
    name: dto.name ?? "",
    brand: dto.brand ?? "",
    model: dto.model ?? "",
    online: Boolean(dto.online),
    isBusy: Boolean(dto.is_busy),
    totalTasks: dto.total_tasks ?? 0,
    successfulTasks: dto.successful_tasks ?? 0,
    failedTasks: dto.failed_tasks ?? 0,
    runningTasks: dto.running_tasks ?? 0,
    uptimeMs: dto.uptime_ms ?? 0,
    busyTimeMs: dto.busy_time_ms ?? 0,
    utilizationPct: toNumber(dto.utilization_pct, 0),
    avgTaskDurationMs: dto.avg_task_duration_ms ?? 0,
  };
}

function mapSummary(dto: FleetSummaryDto): FleetSummary {
  const period = dto.period === "24h" || dto.period === "30d" ? dto.period : "7d";
  return {
    totalDevices: dto.total_devices ?? 0,
    onlineDevices: dto.online_devices ?? 0,
    busyDevices: dto.busy_devices ?? 0,
    freeDevices: dto.free_devices ?? 0,
    offlineDevices: dto.offline_devices ?? 0,
    fleetUtilizationPct: toNumber(dto.fleet_utilization_pct, 0),
    totalTasks: dto.total_tasks ?? 0,
    period,
  };
}

export function mapLoadStats(dto: LoadStatsResponseDto): DeviceLoadStats {
  const data = dto.data ?? {};
  return {
    devices: (data.devices ?? []).map(mapLoadRow),
    summary: mapSummary(data.summary ?? {}),
  };
}

/**
 * Android OS label from the numeric SDK/API level. The marketplace card renders
 * "Android <n>", but the backend's `version` field is the *app* version
 * ("2.210"), not the OS — only `sdkVersion` identifies the platform. Mirrors the
 * legacy marketplace `androidVersions` map. Returns "" for an unknown level, so
 * the card falls back to "Android ?" rather than showing the wrong number.
 */
const ANDROID_BY_SDK: Record<number, string> = {
  36: "16",
  35: "15",
  34: "14",
  33: "13",
  32: "12",
  31: "12",
  30: "11",
  29: "10",
  28: "9",
  27: "8.1",
  26: "8.0",
  25: "7.1",
  24: "7.0",
};

export function androidVersionFromSdk(sdkVersion: number): string {
  return ANDROID_BY_SDK[sdkVersion] ?? "";
}

export function mapMarketDevice(dto: MarketDeviceDto): MarketDevice {
  const price = (v: unknown): number | null =>
    v == null ? null : toNumber(v, 0);
  return {
    deviceId: dto.device_id ?? dto.id ?? "",
    name: dto.name ?? "",
    brand: dto.brand ?? "",
    model: dto.model ?? "",
    ownerUserId: dto.user_id ?? "",
    ownerUsername: dto.username ?? "",
    country: dto.country ?? "",
    sdkVersion: dto.sdkVersion ?? 0,
    androidVersion: androidVersionFromSdk(dto.sdkVersion ?? 0),
    ramMb: dto.ramMb ?? 0,
    numberOfCores: dto.numberOfCores ?? 0,
    processor: dto.processor ?? "",
    isEmulator: Boolean(dto.isEmulator),
    online: Boolean(dto.online),
    prices: {
      hourly: price(dto.rent_price_hourly),
      daily: price(dto.rent_price_daily),
      weekly: price(dto.rent_price_weekly),
      monthly: price(dto.rent_price_monthly),
    },
  };
}

export function mapMarket(
  dto: MarketResponseDto,
  page: number,
  limit: number,
): MarketSearchResult {
  return {
    devices: (dto.devices ?? []).map(mapMarketDevice),
    total: dto.totalDevices ?? 0,
    page,
    limit,
  };
}

export function mapDeviceSettings(dto: DeviceSettingsDto): DeviceSettings {
  const d = dto.device ?? {};
  return {
    remoteDeviceId: d.remote_device_id ?? "",
    name: d.name ?? "",
    online: Boolean(d.online),
    triggerLinks: (d.airplane_trigger_links ?? []).map((l) => ({
      id: l._id ?? "",
      label: l.label ?? "",
      createdAt: l.created_at ?? "",
    })),
    minIntervalSeconds: d.airplane_trigger_min_interval ?? 0,
    autoTriggerMinutes: d.airplane_auto_trigger_interval ?? 0,
    avgRotationDurationSeconds: d.avg_ip_rotation_duration_seconds_7d ?? null,
    avgRotationIntervalSeconds: d.avg_ip_rotation_interval_seconds_7d ?? null,
    rotationCount7d: d.ip_rotation_count_7d ?? 0,
  };
}

function mapTriggerLog(dto: AirplaneTriggerLogDto): AirplaneTriggerLog {
  return {
    triggeredAt: dto.triggered_at ?? "",
    completedAt: dto.completed_at ?? null,
    success: Boolean(dto.success),
    errorMessage: dto.error_message ?? null,
    triggerIp: dto.trigger_ip ?? "",
    deviceIp: dto.device_ip ?? [],
  };
}

export function mapTriggerLogs(dto: TriggerLogsResponseDto): TriggerLogsResult {
  return {
    logs: (dto.logs ?? []).map(mapTriggerLog),
    total: dto.total ?? 0,
  };
}

/** Human labels for rent durations (UI helper, kept beside the mappers). */
export const RENT_DURATION_LABELS: Record<RentDuration, string> = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

export const RENT_DURATION_SUFFIX: Record<RentDuration, string> = {
  hourly: "/hr",
  daily: "/day",
  weekly: "/wk",
  monthly: "/mo",
};

// --- Buckets ---------------------------------------------------------------

export function mapDeviceBucket(
  dto: DeviceBucketResponseDto,
): DeviceBucketData {
  const bucket = dto.deviceBucket;
  return bucket && typeof bucket === "object" && !Array.isArray(bucket)
    ? (bucket as DeviceBucketData)
    : {};
}

// --- Proxy -----------------------------------------------------------------

export function mapProxyConnection(
  dto: ProxyConnectionDto,
  bandwidth?: { bytesIn: number; bytesOut: number },
): ProxyConnection {
  return {
    connectionId: dto.connectionId ?? "",
    password: dto.password ?? "",
    ownerUserId: dto.ownerUserId ?? null,
    name: dto.name ?? null,
    createdAt: dto.createdAt ?? "",
    deviceId: dto.deviceId ?? null,
    bytesIn: bandwidth?.bytesIn ?? 0,
    bytesOut: bandwidth?.bytesOut ?? 0,
  };
}

/** Aggregate raw bandwidth rows by connection id, summing in/out bytes. */
export function aggregateBandwidth(
  dto: ProxyBandwidthResponseDto,
): Record<string, { bytesIn: number; bytesOut: number }> {
  const out: Record<string, { bytesIn: number; bytesOut: number }> = {};
  for (const row of dto.rows ?? []) {
    const id = row.connectionId;
    if (!id) continue;
    const acc = out[id] ?? { bytesIn: 0, bytesOut: 0 };
    acc.bytesIn += toNumber(row.bytesIn, 0);
    acc.bytesOut += toNumber(row.bytesOut, 0);
    out[id] = acc;
  }
  return out;
}

// --- Notifications ---------------------------------------------------------

export function mapDeviceNotification(
  dto: DeviceNotificationDto,
): DeviceNotification {
  const params = (dto.params ?? {}) as DeviceNotification["params"];
  return {
    notificationId: dto.notification_id ?? "",
    name: dto.name ?? params.notificationName ?? "",
    params,
    read: dto.read ?? null,
    added: dto.added ?? "",
  };
}

export function mapDeviceNotifications(
  dto: DeviceNotificationsResponseDto,
): DeviceNotificationsPage {
  return {
    notifications: (dto.notifications ?? []).map(mapDeviceNotification),
    unread: Math.max(0, Math.trunc(toNumber(dto.unread, 0))),
  };
}

// --- Automations -----------------------------------------------------------

export function mapLaunchAutomation(
  dto: LaunchAutomationResponseDto,
): LaunchAutomationResult {
  return {
    success: Boolean(dto.success),
    message: dto.message ?? dto.error ?? "",
  };
}
